/**
 * Why a rubric category has points left that the roadmap will not tell you how
 * to recover.
 *
 * "No fix identified yet" is honest but lazy when we HAVE looked and decided
 * not to ship advice. These are the categories we triaged and deliberately
 * left alone, with the reason a paying user deserves to see. A category with
 * no entry here genuinely has not been triaged.
 *
 * Two rejection classes, both deliberate:
 *
 *  - SCORE-CHASING: a fix exists that moves the number without helping any
 *    real reader or crawler. We do not sell those.
 *  - RE-DERIVED: the category is computed FROM another tool's output rather
 *    than independently measured, so crediting a fix would count one
 *    measurement twice. (Contrast with two tools that each independently
 *    fetch and score the same artifact, which is a real double measurement
 *    and IS credited to both.)
 */

/**
 * KNOWN-OPEN (not built):
 *
 *  - A WAF/Cloudflare challenge page is served as 200 + HTML and is currently
 *    graded as "file missing", so a protected site is told to create a file it
 *    may already have. Separating it from a genuine soft-404 needs body
 *    heuristics (cloudflare / captcha / access-denied markers). Fixtures for
 *    both cases live in radar/llms-reachability.test.tsx.
 *
 * RESOLVED 2026-07-20: ai-readiness's measurability gate now keys off
 * llms-validator's scoreStatus rather than the broad `blocked` flag, so a
 * soft-404 with a dead crawl grades instead of withholding. That closes the
 * blocked/accessFailed family across all three tools.
 *
 * RESOLVED 2026-07-20: crawl-check's soft-404 score inflation. Verifiability
 * for both robots.txt and llms.txt now keys off `accessFailed`, so a soft-404
 * scores 0 in its category instead of being dropped from the ratio. See
 * tools/crawl-soft404.test.tsx and the dated notice in radar/scoring-notices.
 */
export const STRANDED_REASONS: Record<string, string> = {
  'robots::Search Bot Coverage':
    'No honest fix: score-chasing. The category rewards named User-agent blocks for Googlebot, Bingbot, and Applebot, but a named block that merely repeats your wildcard changes no crawler behavior. Worse, it silently stops those bots inheriting future wildcard edits. We will not recommend a change whose only effect is the score.',

  'readiness::llmCommunication':
    'Re-derived, not independently measured. This category is computed from the llms.txt Validator’s own output, so crediting a fix here would count the same measurement a second time. The underlying work is already credited under llms.txt Validator.',

  'readiness::contentAccessibility':
    'Re-derived, not independently measured. This category is computed from AI Crawl Check’s content-access result, so the underlying work is already credited under Crawl Check.',

  'readiness::botDiscoverability':
    'Re-derived, not independently measured. This category feeds two tool scores at once, so crediting a fix here would double-count a single measurement.',

  'schemaAudit::Schema Volume':
    'No standalone fix: this category reflects the number of real typed schema entities found across the audited pages. Add an entity only when it represents real business, place, work, or page content; count-only markup would be score-chasing.',

  'schemaAudit::Type Diversity':
    'No standalone fix: this category reflects supported schema types that genuinely describe the audited site. Adding unused or irrelevant types purely to raise the score would be score-chasing.',
}

export function strandedReason(
  tool: string,
  category: string
): string | undefined {
  return STRANDED_REASONS[`${tool}::${category}`]
}
